import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  forwardRef,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { DropDownItem } from '../../../../../models/shared/stylesheet/drop-down-item';
import { createPopper, Instance, Modifier } from '@popperjs/core';
import { sameWidth } from '../../../../../utils/popper-modifiers/same-width.modifier';
import { map, shareReplay, take } from 'rxjs/operators';
import { CommonModule } from '@angular/common';
import { DropdownItemHighlightedPipe } from './dropdown-item-highlighted.pipe';
import { DropdownOptionIdPipe } from '../../../../../ui/form-components/descriptive-dropdown-input/dropdown-option-id.pipe';

export interface DropDownGroup {
  label: string | null;
  items: DropDownItem[];
}

const OPEN_DROPDOWN_KEYS = ['Enter', ' ', 'ArrowDown', 'ArrowUp'];

let nextListboxId = 0;

@Component({
  selector: 'app-dropdown-input',
  templateUrl: './dropdown-input.component.html',
  styleUrls: ['./dropdown-input.component.scss'],
  standalone: true,
  imports: [CommonModule, DropdownItemHighlightedPipe, DropdownOptionIdPipe],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DropdownInputComponent),
      multi: true
    }
  ],
  changeDetection: ChangeDetectionStrategy.Default
})
export class DropdownInputComponent implements ControlValueAccessor, OnChanges, AfterViewInit, OnDestroy {
  @Input() dropdowns: DropDownItem[] = [];
  @Input() placeholder: string = $localize`Select`;
  @Input() isMobile: boolean = false;
  @Input() disabled: boolean = false;
  @Input() error: boolean = false;
  @Input() clearable: boolean = false;
  @Input() controlId: string | null = null;
  @Input() name: string | null = null;
  @Input() labelId: string | null = null;

  constructor(private _eref: ElementRef, private cdr: ChangeDetectorRef) {}

  @ViewChild('dropdownContent', { static: false }) dropdownContent!: ElementRef<HTMLDivElement>;
  @ViewChild('desktopDropdown', { static: false }) desktopDropdown!: ElementRef<HTMLDivElement>;

  @HostListener('document:click', ['$event'])
  public handleClickOutside(event: MouseEvent): void {
    const target = event.target as Node;
    const insideHost = this._eref.nativeElement.contains(target);
    const insideDropdown = this.dropdownContent?.nativeElement.contains(target);
    if (!insideHost && !insideDropdown) {
      this.closeDropdown();
    }
  }

  private pendingValue: any = null;
  private popperInstance: Instance | undefined;

  public selectedItem$ = new BehaviorSubject<DropDownItem | null>(null);
  public showDropdown$ = new BehaviorSubject<boolean>(false);
  public isMobile$ = new BehaviorSubject<boolean>(false);
  public highlightedIndex: number = -1;

  public readonly listboxId = `dropdown-listbox-${nextListboxId++}`;

  // Presentational view of `dropdowns` collapsed into consecutive category groups. When no item
  // declares a `group`, this is a single group with a null label (rendered without a header).
  public groups: DropDownGroup[] = [];

  private _dropdowns = new BehaviorSubject<DropDownItem[]>([]);

  public readonly interactiveItems$: Observable<(DropDownItem | 'reset')[]> = combineLatest([
    this.selectedItem$,
    this._dropdowns
  ]).pipe(
    map(([selectedItem, dropdowns]) => this.buildInteractiveItems(dropdowns, selectedItem)),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  onChange = (_: any) => {};
  onTouched = () => {};

  writeValue(obj: any): void {
    this.pendingValue = obj;
    this.trySelectValue();
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  public toggleDropdown(): void {
    if (this.disabled) return;

    combineLatest([
      this.showDropdown$.pipe(take(1)),
      this.interactiveItems$.pipe(take(1)),
      this.selectedItem$.pipe(take(1))
    ]).subscribe(([isOpen, items, selectedItem]) => {
      if (isOpen) {
        this.closeDropdown();
        this.highlightedIndex = -1;
      } else {
        this.openDropdown(items, selectedItem);
      }
    });
  }

  public closeDropdown(): void {
    this.showDropdown$.next(false);
    this.popperInstance?.destroy();
  }

  public itemSelected(item: DropDownItem): void {
    if (item.getSelectionIsDisabled()) return;

    this.selectedItem$.next(item);
    this.onChange(item.getSelectionValue());
    this.onTouched();
    this.closeDropdown();
  }

  public resetInput(): void {
    this.selectedItem$.next(null);
    this.onChange(null);
    this.onTouched();
    this.highlightedIndex = -1;
  }

  public onSelectChanged(event: Event): void {
    const val = (event.target as HTMLSelectElement).value;
    const selected = this.dropdowns.find(d => String(d.getSelectionValue()) === val);
    this.selectedItem$.next(selected ?? null);
    this.onChange(selected?.getSelectionValue() ?? null);
    this.onTouched();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.isMobile) {
      this.isMobile$.next(this.isMobile);
      this.closeDropdown();
    }
    if (changes.dropdowns) {
      this.groups = this.buildGroups(this.dropdowns);
      this._dropdowns.next(this.dropdowns ?? []);
      this.selectedItem$.once(item => {
        if (item) {
          this.pendingValue = item.getSelectionValue();
        }
        this.trySelectValue();
      });
    }
  }

  public ngAfterViewInit(): void {
    this.positionDropdown();
  }

  public ngOnDestroy(): void {
    this.popperInstance?.destroy();
    this.dropdownContent?.nativeElement.remove();
  }

  private buildGroups(items: DropDownItem[]): DropDownGroup[] {
    return (items ?? []).reduce<DropDownGroup[]>((groups, item) => {
      const label = item.group ?? null;
      const lastGroup = groups[groups.length - 1];
      if (lastGroup && lastGroup.label === label) {
        lastGroup.items.push(item);
      } else {
        groups.push({ label, items: [item] });
      }
      return groups;
    }, []);
  }

  protected positionDropdown(): void {
    const dropdownElement = this.dropdownContent.nativeElement;
    // Portal to body so the popover escapes ancestor stacking contexts (e.g. sticky table headers).
    if (dropdownElement.parentElement !== document.body) {
      document.body.appendChild(dropdownElement);
    }
    this.popperInstance = createPopper(this.desktopDropdown.nativeElement, dropdownElement, {
      placement: 'bottom-start',
      modifiers: [
        ...this.popperWidthModifiers(),
        {
          name: 'offset',
          options: {
            offset: [0, 10]
          }
        },
        {
          name: 'eventListeners',
          enabled: true,
          options: {
            scroll: true
          }
        },
        // Popper re-evaluates `flip` on every scroll update, which would move the open panel above
        // the field mid-scroll. Keep it anchored below instead.
        {
          name: 'flip',
          enabled: false
        }
      ],
      strategy: 'fixed'
    });
  }

  protected popperWidthModifiers(): Modifier<any, any>[] {
    return [sameWidth];
  }

  protected buildInteractiveItems(
    dropdowns: DropDownItem[],
    selectedItem: DropDownItem | null
  ): (DropDownItem | 'reset')[] {
    const items: (DropDownItem | 'reset')[] = [];
    if (this.clearable && selectedItem) items.push('reset');
    items.push(...dropdowns.filter(item => !item.getSelectionIsDisabled()));
    return items;
  }

  protected initialHighlightedIndex(items: (DropDownItem | 'reset')[], selectedItem: DropDownItem | null): number {
    if (selectedItem === null) {
      return -1;
    }
    return items.findIndex(item => item !== 'reset' && item === selectedItem);
  }

  private openDropdown(
    items: (DropDownItem | 'reset')[],
    selectedItem: DropDownItem | null,
    highlightWithoutSelection: number = -1
  ): void {
    const selectedIndex = this.initialHighlightedIndex(items, selectedItem);
    this.showDropdown$.next(true);
    this.highlightedIndex = selectedIndex === -1 ? highlightWithoutSelection : selectedIndex;
    this.positionDropdown();
  }

  private openDropdownFromKey(key: string, items: (DropDownItem | 'reset')[], selectedItem: DropDownItem | null): void {
    const lastIndex = items.length - 1;
    const arrowDownIndex = Math.min(0, lastIndex);
    const arrowIndex = key === 'ArrowUp' ? lastIndex : arrowDownIndex;
    const isArrowKey = key === 'ArrowDown' || key === 'ArrowUp';
    this.openDropdown(items, selectedItem, isArrowKey ? arrowIndex : -1);
    this.cdr.detectChanges();
    this.scrollToHighlightedItem();
  }

  private trySelectValue(): void {
    if (!this.dropdowns?.length || this.pendingValue === undefined) return;
    const match = this.dropdowns.find(d => d.getSelectionValue() === this.pendingValue);
    if (match) {
      this.selectedItem$.next(match);
      // Mark the value as consumed. Must be `undefined` (not `null`) so the guard above
      // short-circuits on later `dropdowns` re-emits that arrive without a new writeValue —
      // otherwise the current selection would be wrongly cleared (e.g. Province being reset
      // when its options re-emit after the country/selected-country changes).
      this.pendingValue = undefined;
    } else {
      // No matching option yet. Keep `pendingValue` so the selection can still be applied
      // once the options arrive — the value may have been written before its `dropdowns`
      // emission (e.g. Province patched from "Use my address" before the state options for
      // the member's country have propagated through the async pipe).
      this.selectedItem$.next(null);
    }
    this.cdr?.detectChanges();
  }

  @HostListener('keydown', ['$event'])
  public handleKeyDown(event: KeyboardEvent) {
    if (this.disabled || this.isMobile) return;

    combineLatest([
      this.showDropdown$.pipe(take(1)),
      this.interactiveItems$.pipe(take(1)),
      this.selectedItem$.pipe(take(1))
    ]).subscribe(([isOpen, items, selectedItem]) => {
      if (!isOpen) {
        if (OPEN_DROPDOWN_KEYS.includes(event.key)) {
          event.preventDefault();
          this.openDropdownFromKey(event.key, items, selectedItem);
        }
        return;
      }
      if (event.key === 'Tab') {
        this.closeDropdown();
        return;
      }
      if (!items.length) return;

      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          this.highlightedIndex = (this.highlightedIndex + 1) % items.length;
          this.scrollToHighlightedItem();
          break;
        case 'ArrowUp':
          event.preventDefault();
          this.highlightedIndex = (this.highlightedIndex - 1 + items.length) % items.length;
          this.scrollToHighlightedItem();
          break;
        case 'Enter':
          event.preventDefault();
          const highlighted = items[this.highlightedIndex];
          if (highlighted === 'reset') {
            this.resetInput();
          } else if (highlighted) {
            this.itemSelected(highlighted as DropDownItem);
          }
          break;
        case 'Escape':
          event.preventDefault();
          this.closeDropdown();
          break;
      }
    });
  }

  private scrollToHighlightedItem(): void {
    const contentEl = this.dropdownContent?.nativeElement;
    if (!contentEl) return;

    const highlightedEl = contentEl.querySelector('.dropdown-item.highlighted') as HTMLElement;
    if (!highlightedEl) return;

    const contentRect = contentEl.getBoundingClientRect();
    const itemRect = highlightedEl.getBoundingClientRect();
    const offset = 50; // Height of each dropdown item
    // Scroll down if item is below the visible area
    // We are using double the offset here and below so the item remains in the middle of the list
    if (itemRect.bottom + offset * 2 > contentRect.bottom) {
      contentEl.scrollTop += itemRect.bottom - contentRect.bottom + offset;
    }
    // Scroll up if item is above the visible area
    if (itemRect.top - offset * 2 < contentRect.top) {
      contentEl.scrollTop -= contentRect.top - itemRect.top + offset;
    }
  }

  public trackByValue(index: number, item: DropDownItem): DropDownItem['value'] {
    return item.getSelectionValue();
  }

  public trackByGroupLabel(index: number, group: DropDownGroup): string {
    return group.label ?? `__ungrouped_${index}`;
  }
}
